<sb-sidebar [minimizable]="true" [(isMinimized)]="isMinimized" [animated]="false" [labsLayout]="true">
  <sb-sidebar-header [iconOnlyWhenMinimized]="true">
    <a routerLink="/dashboard" sb-sidebar-header-icon class="size-full">
      <sb-logo class="size-full" [variant]="'icon'" [alt]="'Shiftbase logo'" />
    </a>
    <a routerLink="/dashboard" sb-sidebar-header-content class="flex items-center">
      <sb-logo class="w-32" />
    </a>
  </sb-sidebar-header>

  <ul sb-sidebar-list sbDisclosureFlyoutGroup [ariaLabel]="'Main navigation' | translate">
    @if (commandPaletteEnabled()) {
      <li>
        <button
          type="button"
          sb-sidebar-item
          [icon]="'search-cute-re'"
          [disableTooltip]="true"
          [sbTooltip]="'Search' | translate"
          [sbTooltipHotkeys]="commandPaletteShortcut"
          [sbTooltipPlacement]="'right'"
          [sbTooltipDelay]="'none'"
          [sbTooltipDisabled]="!isMinimized()"
          [attr.aria-keyshortcuts]="commandPaletteAriaShortcut"
          (click)="openCommandPalette()"
        >
          {{ 'Search' | translate }}
          @if (commandPaletteShortcut) {
            <!-- Decorative hint (aria-hidden): the button keeps "Search" as its accessible name. -->
            <span class="ml-auto flex gap-2xs" aria-hidden="true">
              @for (key of commandPaletteShortcut; track key) {
                <sb-badge size="xsmall" color="grey">{{ key }}</sb-badge>
              }
            </span>
          }
        </button>
      </li>
    }
    @for (item of navItems(); track item.labelKey) {
      <li class="relative">
        @if (item.subItems?.length) {
          <a
            sb-sidebar-item
            [routerLink]="item.link"
            [active]="item.isActive"
            [icon]="item.icon"
            [activeIcon]="item.activeIcon"
            [hasSubItems]="true"
            [sbDisclosureFlyoutTriggerFor]="submenu"
            [sbDisclosureFlyoutTriggerOpenDelay]="100"
            [sbDisclosureFlyoutTriggerSwapDelay]="100"
            [sbDisclosureFlyoutTriggerCloseDelay]="0"
            #submenuTrigger="sbDisclosureFlyoutTrigger"
            (keydown.enter)="submenuTrigger.dismiss()"
          >
            {{ item.labelKey }}
            @if (item.beta) {
              <sb-badge class="ml-2" [color]="'brandInverted'">BETA</sb-badge>
            }
            @if (item.badgeCount?.(); as count) {
              <sb-notification-badge class="ml-2" [count]="count" color="brandInverted" />
            }
          </a>
          <!-- Collapsing on activation matters for pointer users: a clicked link keeps focus, and
               focus inside the panel would otherwise hold it open over the page just navigated to. -->
          <!-- Shifted up so the first sub-item, not the caption, lines up with the parent entry. -->
          <sb-disclosure-flyout-panel #submenu class="-translate-y-10" [gap]="'wide'">
            <!-- -my-xs cancels the panel surface's own vertical padding, leaving p-md all round. -->
            <div class="-my-xs flex flex-col gap-xs p-md">
              <!-- Visible caption only: the list below carries the same name for assistive tech. -->
              <span class="text-sm font-semibold text-subtle uppercase" aria-hidden="true">
                {{ item.labelKey }}
              </span>
              <ul sb-sidebar-list [ariaLabel]="item.labelKey">
                @for (subItem of item.subItems; track subItem.labelKey) {
                  <li>
                    <a
                      sb-sidebar-item
                      [routerLink]="subItem.link"
                      [active]="subItem.isActive"
                      [icon]="subItem.icon"
                      [activeIcon]="subItem.activeIcon"
                      [disableTooltip]="true"
                      [isSubItem]="true"
                      (click)="submenuTrigger.dismiss()"
                    >
                      {{ subItem.labelKey | translate }}
                    </a>
                  </li>
                }
              </ul>
            </div>
          </sb-disclosure-flyout-panel>
        } @else {
          <a
            sb-sidebar-item
            [routerLink]="item.locked ? null : item.link"
            [active]="item.isActive"
            [icon]="item.icon"
            [activeIcon]="item.activeIcon"
            [locked]="!!item.locked"
            [attr.role]="item.locked ? 'link' : null"
            [attr.tabindex]="item.locked ? 0 : null"
            (click)="item.locked && handleLockedClick(item)"
            (keydown.enter)="item.locked && handleLockedClick(item)"
          >
            {{ item.labelKey }}
            @if (item.beta) {
              <sb-badge class="ml-2" [color]="'brandInverted'">BETA</sb-badge>
            }
            @if (item.badgeCount?.(); as count) {
              <sb-notification-badge class="ml-2" [count]="count" color="brandInverted" />
            }
          </a>
        }
      </li>
    }
  </ul>

  <ul sb-sidebar-list sb-sidebar-bottom-list [ariaLabel]="'Support and settings' | translate">
    @if (checklist()) {
      <li>
        <!-- temp overrides -->
        <a
          sb-sidebar-item
          [active]="isSidePanelActive('checklist')"
          [icon]="'rocket-cute-re'"
          [togglesSidePanel]="true"
          (click)="openChecklist()"
          (keydown.enter)="openChecklist()"
          (keydown.space)="openChecklist()"
          tabindex="0"
        >
          <sb-icon
            [name]="isSidePanelActive('checklist') ? 'rocket-cute-fi' : 'rocket-cute-re'"
            size="xl"
            class="shrink-0 text-success"
            sidebar-item-icon
          />
          <div class="flex w-full flex-col gap-xs">
            <span class="-my-2xs text-sm text-default">{{ 'Get started' | translate }}</span>
            <sb-progress-bar [level]="checklistProgressLevel()" size="sm" />
          </div>
        </a>
      </li>
    }
    @if (hasManageAccountPermission() && isUnconfirmedOrFreePlan()) {
      <li>
        <app-side-nav-upsell />
      </li>
    }
    @if (showFeedbackSurvey()) {
      <li>
        <button sb-sidebar-item [icon]="'horn-2-cute-re'" (click)="openFeedbackSurvey()">
          {{ 'Give feedback' | translate }}
        </button>
      </li>
    }
    @if (shouldShowCelloLauncher()) {
      <li>
        <button
          sb-sidebar-item
          [icon]="'gift-cute-re'"
          id="cello-launcher-button"
          (click)="handleReferralProgramClick()"
        >
          {{ 'Refer and earn' | translate }}
        </button>
      </li>
    }
    <li>
      <button
        sb-sidebar-item
        [togglesSidePanel]="true"
        [active]="isSidePanelActive('support')"
        [icon]="'question-cute-re'"
        [activeIcon]="'question-cute-fi'"
        (click)="openHelpCenter()"
      >
        {{ 'Get support' | translate }}
      </button>
    </li>
    @if (hasManageAccountPermission() || hasAppCenterPermission()) {
      <li>
        <a
          sb-sidebar-item
          [icon]="'settings-3-cute-re'"
          [activeIcon]="'settings-3-cute-fi'"
          [routerLink]="
            !hasManageAccountPermission() && hasAppCenterPermission() ? '/account/integrations' : '/account/settings'
          "
          routerLinkActive
          #settingsRla="routerLinkActive"
          [active]="settingsRla.isActive"
        >
          {{ 'Settings' | translate }}
        </a>
      </li>
    }
  </ul>

  <app-side-nav-profile ngProjectAs="sb-sidebar-footer" />
</sb-sidebar>
